import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import pluralize from 'pluralize';
import { usePopulate } from 'api/APIHooks';
import { Card, CardHeader } from 'common/Card';
import formatShortDate from 'common/utils/Dates/formatShortDate';
import { LineItem } from 'common/display/LineItem';
import DollarAmount from 'common/display/Money/DollarAmount';

const VOIDED_CARD_MESSAGE =
  'This authorization has been voided. To view related requests, go to the History section below.';

const ServiceAuthorizationVoidedCard = ({ authorization }) => {
  const { isLoading: isFspLoading } = usePopulate(
    'fee_schedule_program',
    'fee_schedule_program',
    authorization,
    { queryConfig: { placeholderData: undefined } },
  );

  const isSingleDateRequested = authorization.approved_starts_at === authorization.approved_ends_at;
  const isCostBased = !!authorization.approved_cents;
  const unit = get(authorization, 'fee_schedule_program.unit', '');
  const cmCodeRequired = get(authorization, 'fee_schedule_program.cm_code_required_on_authorization', false);

  return (
    <Card
      borderColor="border-action-blue"
      className="px-2 py-2 bg-white"
      data-testid="auth-void-card"
    >
      <CardHeader
        className="pt-2 space-y-2"
        iconColor="#182A44"
        titleIcon="IconMinusCircle"
        title="Authorization Voided"
        subtitle={VOIDED_CARD_MESSAGE}
      />
      <dl className="m-0 px-4 py-6 grid grid-cols-4 gap-y-2 gap-x-12">
        {isCostBased ? (
          <LineItem field="Authorized Amount" valueClassName="col-span-3 ml-2">
            <DollarAmount convertCents className="font-medium-font" value={authorization.approved_cents} />
          </LineItem>
        ) : (
          <LineItem field="Authorized Amount" valueClassName="col-span-3 ml-2" isLoaded={!!unit}>
            {authorization.approved_unit_amount}
            {' '}
            {pluralize(unit, authorization.approved_unit_amount)}
          </LineItem>
        )}
        <LineItem field="Authorized Service Delivery Date(s)" valueClassName="col-span-3 ml-2">
          {isSingleDateRequested ? (
            formatShortDate(authorization.approved_ends_at)
          ) : (
            `${formatShortDate(authorization.approved_starts_at)} - ${formatShortDate(authorization.approved_ends_at)}`
          )}
        </LineItem>
        {!isFspLoading && cmCodeRequired && (
          <LineItem field="ICD-10 Code" valueClassName="col-span-3 ml-2">
            {authorization.clinical_modifications?.map((cm) => cm.code).join(',')}
          </LineItem>
        )}
        <LineItem field="Payer Authorization ID" valueClassName="col-span-3 ml-2">
          {authorization.payer_authorization_number || 'N/A'}
        </LineItem>
        {authorization.reviewer_note && (
          <LineItem field="Note" valueClassName="col-span-3 ml-2">
            <div className="whitespace-pre-line">{authorization.reviewer_note}</div>
          </LineItem>
        )}
      </dl>
    </Card>
  );
};

ServiceAuthorizationVoidedCard.propTypes = {
  authorization: PropTypes.object.isRequired,
};

export default ServiceAuthorizationVoidedCard;
